import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      // Use the API base URL from environment or fallback
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await axios.post(`${baseURL}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Reset link sent! Check your email.');
    } catch (err) {
      const message = err.response?.data?.message || 'Something went wrong. Please try again later.';
      toast.error(message);
      console.error('Forgot password error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setSubmitted(false);
    setEmail('');
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <Link to="/login" className="inline-flex items-center text-sm text-gray-600 hover:text-[var(--primary-color)] transition-colors">
            <FiArrowLeft className="mr-2" />
            Back to Login
          </Link>
        </div>
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Forgot Password?</h2>
          <p className="mt-2 text-sm text-gray-600">Enter your email and we'll send you a link to reset your password</p>
        </div>

        {!submitted ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none relative block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent text-base"
                  placeholder="Enter your registered email"
                  disabled={loading}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[var(--primary-color)] hover:bg-[var(--hover-primary)]'} transition-colors duration-200`}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        ) : (
          <div className="mt-8 space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <h3 className="text-lg font-medium text-green-800 mb-2">Reset link sent!</h3>
              <p className="text-sm text-green-700 mb-2">We've sent a password reset link to:</p>
              <p className="font-semibold text-green-800 bg-green-100 px-4 py-2 rounded-md">{email}</p>
              <p className="mt-4 text-green-700">Please check your inbox and follow the instructions.</p>
            </div>
            <div className="flex flex-col space-y-3">
              <Link to="/login" className="w-full text-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[var(--primary-color)] hover:bg-[var(--hover-primary)] transition-colors duration-200">Return to Login</Link>
              <button onClick={handleResend} className="text-sm text-[var(--primary-color)] hover:text-[var(--hover-primary)] font-medium transition-colors">Didn't receive? Click to resend</button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center border-t pt-6">
          <p className="text-sm text-gray-500">Need help? <Link to="/contact" className="text-[var(--primary-color)] hover:text-[var(--hover-primary)] font-medium">Contact Support</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;